import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import MarkupPanel from './MarkupPanel'
import { DEFAULT_CATEGORIES } from '../lib/defaults'
import LineItem from './LineItem'
import HartCatalogue from './HartCatalogue'
import LabourSection from './LabourSection'
import { NameModal } from './Modals'
import '../styles.css'

const CAT_COLOR = {
  timber: 'var(--c-timber)',
  fixings: 'var(--c-fixings)',
  adhesives: 'var(--c-adhesives)',
  site: 'var(--c-site)',
  additional: 'var(--c-additional)',
  labour: 'var(--c-timber)',
}

const catColor = (cat) => {
  const key = cat.slug || cat.id
  return CAT_COLOR[key] || 'var(--ink-3)'
}

// Standard columns: drag / item / unit cost / qty / total / actions
const COLS_BASE = '22px minmax(0,1fr) 96px 64px 116px 90px'
// Fixings columns: drag / item / supplier / SKU / unit / unit cost / qty / total / actions
const COLS_EXTRA = '22px minmax(0,1fr) 104px 60px 54px 92px 60px 116px 90px'

// Per-section animated body component
function SectionBody({ isOpen, children }) {
  const bodyRef = useRef(null)
  const [h, setH] = useState('auto')

  useLayoutEffect(() => {
    const el = bodyRef.current
    if (!el) return
    if (isOpen) {
      const target = el.scrollHeight
      setH(target + 'px')
      const t = setTimeout(() => setH('auto'), 300)
      return () => clearTimeout(t)
    } else {
      setH(el.scrollHeight + 'px')
      requestAnimationFrame(() => requestAnimationFrame(() => setH('0px')))
    }
  }, [isOpen])

  return (
    <div className="sec-body" ref={bodyRef} style={{ height: h }}>
      {children}
    </div>
  )
}

export default function ProjectScreen() {
  const { id } = useParams()
  const idRef = useRef(id)
  useEffect(() => { idRef.current = id }, [id])
  const navigate = useNavigate()

  const [project, setProject] = useState(null)
  const [sections, setSections] = useState([])  // project_sections rows
  const [lineItems, setLineItems] = useState([])
  const [labourLines, setLabourLines] = useState([])
  const [timeTasks, setTimeTasks] = useState([])
  const [taskCatalogue, setTaskCatalogue] = useState([])
  const [loading, setLoading] = useState(true)
  const [openCategories, setOpenCategories] = useState([])
  const [hartOpen, setHartOpen] = useState(false)
  const [estimatorOpen, setEstimatorOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [savedTemplate, setSavedTemplate] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [updatingPrices, setUpdatingPrices] = useState(false)
  const [nameModal, setNameModal] = useState(null)
  const [pricesUpdatedMsg, setPricesUpdatedMsg] = useState(null)
  const menuRef = React.useRef(null)
  const [addItemsDialog, setAddItemsDialog] = useState(null)
  const [addItemsTab, setAddItemsTab] = useState('catalogue')
  const [inactiveCatalogueItems, setInactiveCatalogueItems] = useState([])
  const [hartEditBasket, setHartEditBasket] = useState(null)
  const [supplierDialog, setSupplierDialog] = useState(false)
  const [supplierInput, setSupplierInput] = useState('')
  const [supplierSent, setSupplierSent] = useState(false)
  const [undoToast, setUndoToast] = useState(null)
  const pendingDeleteRef = useRef(null)

  useEffect(() => { loadAll() }, [id])

  const reloadLineItems = async () => {
    const { data } = await supabase.from('line_items').select('*').eq('project_id', idRef.current).order('sort_order')
    if (data) setLineItems(data)
  }

  const loadAll = async () => {
    const [projRes, itemsRes, labourRes, tasksRes, catRes] = await Promise.all([
      supabase.from('projects').select('*').eq('id', id).single(),
      supabase.from('line_items').select('*').eq('project_id', id).order('sort_order'),
      supabase.from('labour_lines').select('*').eq('project_id', id).order('sort_order'),
      supabase.from('time_tasks').select('*').eq('project_id', id).order('sort_order'),
      supabase.from('task_catalogue').select('*').order('sort_order'),
    ])

    if (projRes.error) { navigate('/projects'); return }

    // Ensure day_rate and margin have sensible defaults if missing from DB
    const projData = {
      ...projRes.data,
      day_rate: projRes.data.day_rate ?? 350,
      hours_per_day: projRes.data.hours_per_day ?? 8,
      labour_markup_pct: projRes.data.labour_markup_pct ?? 0,
      material_markup_pct: projRes.data.material_markup_pct ?? 20,
      vat_registered: projRes.data.vat_registered ?? false,
      vat_rate: projRes.data.vat_rate ?? 20,
    }
    setProject(projData)

    // Load or create project sections (graceful fallback if table missing)
    let sectionsData = null
    try {
      const { data: sData, error: sErr } = await supabase.from('project_sections')
        .select('*').eq('project_id', id).order('sort_order')
      if (!sErr) sectionsData = sData
    } catch(e) { sectionsData = null }

    if (!sectionsData || sectionsData.length === 0) {
      const defaultSections = [
        { project_id: id, name: 'Timber', slug: 'timber', color: '#8B5E3C', sort_order: 1 },
        { project_id: id, name: 'Fixings & Hardware', slug: 'fixings', color: '#1a7a8a', sort_order: 2 },
        { project_id: id, name: 'Adhesives, Sealants & Finishes', slug: 'adhesives', color: '#9b59b6', sort_order: 3 },
        { project_id: id, name: 'Site Materials', slug: 'site', color: '#5d6d7e', sort_order: 4 },
        { project_id: id, name: 'Additional', slug: 'additional', color: '#e74c3c', sort_order: 5 },
      ]
      try {
        const { data: created } = await supabase.from('project_sections').insert(defaultSections).select()
        sectionsData = created || defaultSections
      } catch(e) { sectionsData = defaultSections }
    }
    // Deduplicate by slug, then add Labour virtual section
    const unique = sectionsData.filter((s, i, arr) => arr.findIndex(x => x.slug === s.slug) === i)
    const withLabour = unique.some(s => s.slug === 'labour')
      ? unique
      : [...unique, { id: 'labour', slug: 'labour', name: 'Labour', color: '#9a6b1e', sort_order: unique.length + 1, virtual: true }]
    setSections(withLabour)
    if (withLabour.length > 0) setOpenCategories([withLabour[0].slug || withLabour[0].id])

    const existingItems = itemsRes.data || []
    setLineItems(existingItems)

    let labourData = labourRes.data || []
    // Deduplicate by name to guard against double-insert from dev strict mode
    const uniqueLabour = labourData.filter((l, i, arr) => arr.findIndex(x => x.name === l.name) === i)
    setLabourLines(uniqueLabour)
    setTimeTasks(tasksRes.data || [])
    setTaskCatalogue(catRes.data || [])
    setLoading(false)
  }

  // ── Calculations ─────────────────────────────────────────────

  const categoryTotal = (categoryId) => {
    return lineItems
      .filter(item => item.category === categoryId)
      .reduce((sum, item) => sum + (item.unit_cost || 0) * (item.quantity || 0), 0)
  }

  const allMaterialsTotal = () => {
    return sections.filter(sec => (sec.slug || sec.id) !== 'labour').reduce((sum, sec) => sum + categoryTotal(sec.slug || sec.id), 0)
  }

  const estimatorTotal = useCallback(() => {
    const totalHours = timeTasks.reduce((sum, task) => {
      const hours = task.is_multiplied
        ? (task.unit_duration || 0) * (task.quantity || 1)
        : (task.unit_duration || 0)
      return sum + hours
    }, 0)
    return totalHours / (project?.hours_per_day || 8)
  }, [timeTasks, project?.hours_per_day])

  const labourTotal = () => {
    const manual = labourLines.reduce((sum, line) => {
      const days = line.from_estimator ? estimatorTotal() : (line.days || 0)
      return sum + days * (project?.day_rate || 350)
    }, 0)
    // Include orphan B&I days when no from_estimator labour line exists
    const hasFromEstimator = labourLines.some(l => l.from_estimator)
    const orphanDays = !hasFromEstimator && timeTasks.length > 0 ? estimatorTotal() : 0
    return manual + orphanDays * (project?.day_rate || 350)
  }

  const marginOnLabour = () => {
    return labourTotal() * ((project?.labour_markup_pct || 0) / 100)
  }

  const materialMarkup = () => {
    return allMaterialsTotal() * ((project?.material_markup_pct || 0) / 100)
  }

  const vatAmount = () => {
    if (!project?.vat_registered) return 0
    return (allMaterialsTotal() + materialMarkup() + labourTotal() + marginOnLabour()) * ((project?.vat_rate ?? 20) / 100)
  }

  const grandTotal = () => {
    return allMaterialsTotal() + materialMarkup() + labourTotal() + marginOnLabour() + vatAmount()
  }

  // ── Project field updates ─────────────────────────────────────

  const updateProject = async (field, value) => {
    const update = { [field]: value }
    setProject(prev => ({ ...prev, ...update }))
    await supabase.from('projects').update(update).eq('id', idRef.current)
  }

  // ── Drag reorder for sections ───────────────────────────────────
  const [dragSectionId, setDragSectionId] = useState(null)
  const [dragOverSectionId, setDragOverSectionId] = useState(null)

  const handleSectionDragStart = (e, sectionId) => {
    setDragSectionId(sectionId)
    e.dataTransfer.effectAllowed = 'move'
    e.stopPropagation()
  }
  const handleSectionDragOver = (e, sectionId) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverSectionId(sectionId)
  }
  const handleSectionDrop = async (e, targetId) => {
    e.preventDefault()
    e.stopPropagation()
    if (!dragSectionId || dragSectionId === targetId) {
      setDragSectionId(null); setDragOverSectionId(null); return
    }
    const fromIdx = sections.findIndex(s => s.id === dragSectionId)
    const toIdx = sections.findIndex(s => s.id === targetId)
    if (fromIdx < 0 || toIdx < 0) { setDragSectionId(null); setDragOverSectionId(null); return }
    const reordered = [...sections]
    const [moved] = reordered.splice(fromIdx, 1)
    reordered.splice(toIdx, 0, moved)
    const updated = reordered.map((s, i) => ({ ...s, sort_order: i + 1 }))
    setSections(updated)
    for (const s of updated) {
      if (!s.virtual) {  // Don't try to update virtual sections (Labour) in DB
        await supabase.from('project_sections').update({ sort_order: s.sort_order }).eq('id', s.id)
      }
    }
    setDragSectionId(null); setDragOverSectionId(null)
  }

  // ── Drag reorder for line items ─────────────────────────────────
  const [dragLineId, setDragLineId] = useState(null)
  const [dragOverLineId, setDragOverLineId] = useState(null)

  const handleLineDragStart = (e, itemId) => {
    setDragLineId(itemId)
    e.dataTransfer.effectAllowed = 'move'
  }
  const handleLineDragOver = (e, itemId) => {
    e.preventDefault()
    setDragOverLineId(itemId)
  }
  const handleLineDrop = async (e, targetId, targetCategoryId) => {
    e.preventDefault()
    if (!dragLineId || dragLineId === targetId) { setDragLineId(null); setDragOverLineId(null); return }
    const draggedItem = lineItems.find(i => i.id === dragLineId)
    if (!draggedItem) { setDragLineId(null); setDragOverLineId(null); return }

    const sourceCategoryId = draggedItem.category
    const crossSection = sourceCategoryId !== targetCategoryId

    if (crossSection) {
      // Move item to target section, place at end of that section
      const targetCatItems = lineItems.filter(i => i.category === targetCategoryId)
        .sort((a, b) => a.sort_order - b.sort_order)
      const newSortOrder = targetCatItems.length
      await supabase.from('line_items').update({ category: targetCategoryId, sort_order: newSortOrder }).eq('id', dragLineId)
      setLineItems(prev => prev.map(i => i.id === dragLineId
        ? { ...i, category: targetCategoryId, sort_order: newSortOrder }
        : i
      ))
    } else {
      // Same section — reorder within section
      const catItems = lineItems.filter(i => i.category === targetCategoryId)
        .sort((a, b) => a.sort_order - b.sort_order)
      const fromIdx = catItems.findIndex(i => i.id === dragLineId)
      const toIdx = catItems.findIndex(i => i.id === targetId)
      if (fromIdx < 0 || toIdx < 0) { setDragLineId(null); setDragOverLineId(null); return }
      const reordered = [...catItems]
      const [moved] = reordered.splice(fromIdx, 1)
      reordered.splice(toIdx, 0, moved)
      const updated = reordered.map((item, idx) => ({ ...item, sort_order: idx }))
      setLineItems(prev => [
        ...prev.filter(i => i.category !== targetCategoryId),
        ...updated
      ])
      for (const item of updated) {
        await supabase.from('line_items').update({ sort_order: item.sort_order }).eq('id', item.id)
      }
    }
    setDragLineId(null)
    setDragOverLineId(null)
  }

  const addLineItem = async (categoryId, overrides = {}) => {
    const maxOrder = lineItems.filter(i => i.category === categoryId).length
    const { data, error } = await supabase.from('line_items').insert({
      project_id: idRef.current,
      category: categoryId,
      description: '',
      unit_cost: 0,
      quantity: 1,
      procurement: 'order',
      sort_order: maxOrder,
      ...overrides,
    }).select().single()
    if (!error && data) setLineItems(prev => [...prev, data])
  }

  const addLineItemAfter = async (categoryId, afterSortOrder) => {
    const catItems = lineItems.filter(i => i.category === categoryId)
      .sort((a, b) => a.sort_order - b.sort_order)
    const insertAt = catItems.findIndex(i => i.sort_order === afterSortOrder) + 1
    // Shift sort_order of items after insertion point
    const toShift = catItems.slice(insertAt)
    for (const item of toShift) {
      await supabase.from('line_items').update({ sort_order: item.sort_order + 1 }).eq('id', item.id)
    }
    setLineItems(prev => prev.map(i =>
      i.category === categoryId && i.sort_order >= insertAt
        ? { ...i, sort_order: i.sort_order + 1 } : i
    ))
    const { data, error } = await supabase.from('line_items').insert({
      project_id: idRef.current, category: categoryId,
      description: '', unit_cost: 0, quantity: 1,
      procurement: 'order', sort_order: insertAt,
    }).select().single()
    if (!error && data) setLineItems(prev => [...prev, data])
  }

  const updateLineItem = async (itemId, updates) => {
    setLineItems(prev => prev.map(i => i.id === itemId ? { ...i, ...updates } : i))
    await supabase.from('line_items').update(updates).eq('id', itemId)
  }

  const scheduleDelete = (label, restoreFn, commitFn) => {
    if (pendingDeleteRef.current) {
      clearTimeout(pendingDeleteRef.current.timeoutId)
      pendingDeleteRef.current.commitFn()
    }
    const timeoutId = setTimeout(() => {
      commitFn()
      setUndoToast(null)
      pendingDeleteRef.current = null
    }, 6000)
    pendingDeleteRef.current = { timeoutId, commitFn }
    setUndoToast({
      label,
      restore: () => {
        clearTimeout(timeoutId)
        pendingDeleteRef.current = null
        setUndoToast(null)
        restoreFn()
      }
    })
  }

  const deleteLineItem = (itemId) => {
    const item = lineItems.find(i => i.id === itemId)
    if (!item) return
    setLineItems(prev => prev.filter(i => i.id !== itemId))
    supabase.from('line_items').delete().eq('id', itemId).then()
    scheduleDelete(
      `"${item.description || 'Item'}" deleted`,
      async () => {
        const { id: _id, created_at: _c, updated_at: _u, ...rest } = item
        const { data: restored } = await supabase.from('line_items').insert(rest).select().single()
        setLineItems(prev => [...prev, restored || item].sort((a, b) => a.sort_order - b.sort_order))
      },
      () => {} // no-op: DB delete already committed above
    )
  }

  // ── Hart catalogue integration ────────────────────────────────

  const handleHartItems = async (basketItems) => {
    const basketSubtotal = basketItems.reduce((sum, b) => sum + b.price * b.qty, 0)
    const deliveryCost = basketSubtotal < 100 ? 7 : 0
    const baseTotal = basketSubtotal + deliveryCost
    // VAT-registered: store ex-VAT so project-level VAT applies correctly.
    // Not VAT-registered: store inc-VAT because Kelsey pays it and can't reclaim.
    const vatRate = (project?.vat_rate ?? 20) / 100
    const totalCost = project?.vat_registered ? baseTotal : baseTotal * (1 + vatRate)

    const itemCount = basketItems.reduce((sum, b) => sum + b.qty, 0)
    const itemSummary = basketItems.length <= 3
      ? basketItems.map(b => b.name).join(', ')
      : `${basketItems.length} items`

    const orderId = `hart_${Date.now()}`
    const basketJson = JSON.stringify(basketItems)

    await addLineItem('fixings', {
      description: `Hart Wholesale — ${itemSummary}`,
      unit_cost: parseFloat(totalCost.toFixed(2)),
      quantity: 1,
      procurement: 'order',
      supplier: 'Hart Wholesale',
      hart_order_id: orderId,
      hart_basket: basketJson,
    })

    setHartOpen(false)
    setHartEditBasket(null)
  }

  const openHartForEdit = (item) => {
    // Load basket from the bundled line item and delete it ready for re-adding
    if (item.hart_basket) {
      try { setHartEditBasket(JSON.parse(item.hart_basket)) }
      catch { setHartEditBasket(null) }
    }
    setLineItems(prev => prev.filter(i => i.id !== item.id))
    supabase.from('line_items').delete().eq('id', item.id).then()
    setHartOpen(true)
  }

  // ── Labour lines ─────────────────────────────────────────────

  const updateLabourLine = async (lineId, days) => {
    const daysNum = parseFloat(days) || 0
    setLabourLines(prev => prev.map(l => l.id === lineId ? { ...l, days: daysNum } : l))
    await supabase.from('labour_lines').update({ days: daysNum }).eq('id', lineId)
  }

  const addLabourLine = async () => {
    const { data, error } = await supabase.from('labour_lines').insert({
      project_id: idRef.current,
      name: 'New line',
      days: 0,
      from_estimator: false,
      sort_order: labourLines.length,
    }).select().single()
    if (!error && data) setLabourLines(prev => [...prev, data])
  }

  const deleteLabourLine = (lineId) => {
    const line = labourLines.find(l => l.id === lineId)
    if (!line) return
    setLabourLines(prev => prev.filter(l => l.id !== lineId))
    supabase.from('labour_lines').delete().eq('id', lineId).then()
    scheduleDelete(
      `"${line.task || 'Labour line'}" deleted`,
      async () => {
        const { id: _id, created_at: _c, updated_at: _u, ...rest } = line
        const { data: restored } = await supabase.from('labour_lines').insert(rest).select().single()
        setLabourLines(prev => [...prev, restored || line].sort((a, b) => a.sort_order - b.sort_order))
      },
      () => {}
    )
  }

  // ── Time tasks ───────────────────────────────────────────────

  // When estimator updates, also update the B&I labour line days
  const handleTasksChange = async (newTasks) => {
    setTimeTasks(newTasks)
    const totalDays = newTasks.reduce((sum, task) => {
      return sum + (task.is_multiplied
        ? (task.unit_duration || 0) * (task.quantity || 1)
        : (task.unit_duration || 0))
    }, 0)
    // Update the Build & Install labour line
    const biLine = labourLines.find(l => l.from_estimator)
    if (biLine) {
      await supabase.from('labour_lines').update({ days: totalDays }).eq('id', biLine.id)
      setLabourLines(prev => prev.map(l => l.from_estimator ? { ...l, days: totalDays } : l))
    }
  }

  // ── Close menu on outside click ──────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // ── Open as New Project ──────────────────────────────────────────
  const openAsNewProject = async () => {
    setMenuOpen(false)
    setNameModal({
      title: 'Name for new project',
      placeholder: 'Project name…',
      defaultValue: project?.name || '',
      confirmLabel: 'Create',
      onConfirm: async (name) => {
        const { data: items } = await supabase.from('line_items').select('*').eq('project_id', idRef.current).order('sort_order')
        const { data: labour } = await supabase.from('labour_lines').select('*').eq('project_id', idRef.current).order('sort_order')
        const { data: tasks } = await supabase.from('time_tasks').select('*').eq('project_id', idRef.current).order('sort_order')
        const { data: sourceSections } = await supabase.from('project_sections').select('*').eq('project_id', idRef.current).order('sort_order')
        const { data: newProj, error } = await supabase.from('projects').insert({
          user_id: project.user_id,
          name: name.trim(),
          status: 'enquiry',
          is_template: false,
          day_rate: project.day_rate,
          hours_per_day: project.hours_per_day || 8,
          material_markup_pct: project.material_markup_pct ?? 20,
          labour_markup_pct: project.labour_markup_pct || 0,
          vat_registered: project.vat_registered || false,
          vat_rate: project.vat_rate ?? 20,
        }).select().single()
        if (error || !newProj) return
        const strip = ({ id: _id, project_id: _pid, created_at: _c, updated_at: _u, ...rest }) => ({ ...rest, project_id: newProj.id })
        if (sourceSections?.length) await supabase.from('project_sections').insert(sourceSections.map(strip))
        if (items?.length) await supabase.from('line_items').insert(items.map(strip))
        if (labour?.length) await supabase.from('labour_lines').insert(labour.map(strip))
        if (tasks?.length) await supabase.from('time_tasks').insert(tasks.map(strip))
        navigate(`/projects/${newProj.id}`)
      }
    })
  }

  // ── Update Prices from My Items ──────────────────────────────────
  const updatePrices = async () => {
    setMenuOpen(false)
    setUpdatingPrices(true)
    const { data: { user } } = await supabase.auth.getUser()
    const { data: catalogue } = await supabase.from('item_catalogue')
      .select('description, category, unit_cost').eq('user_id', user.id).eq('is_active', true)
    if (!catalogue?.length) {
      setUpdatingPrices(false)
      setPricesUpdatedMsg('My Items is empty')
      setTimeout(() => setPricesUpdatedMsg(null), 3000)
      return
    }
    const lookup = {}
    catalogue.forEach(c => { lookup[`${c.description.toLowerCase()}|${c.category}`] = c.unit_cost })
    let count = 0
    for (const item of lineItems) {
      const key = `${(item.description || '').toLowerCase()}|${item.category}`
      if (lookup[key] !== undefined && lookup[key] !== item.unit_cost) {
        await supabase.from('line_items').update({ unit_cost: lookup[key] }).eq('id', item.id)
        setLineItems(prev => prev.map(i => i.id === item.id ? { ...i, unit_cost: lookup[key] } : i))
        count++
      }
    }
    setUpdatingPrices(false)
    setPricesUpdatedMsg(count > 0 ? `${count} price${count > 1 ? 's' : ''} updated from My Items` : 'No matching items in My Items')
    setTimeout(() => setPricesUpdatedMsg(null), 3000)
  }

  // ── Copy total ───────────────────────────────────────────────

  const copyTotal = () => {
    navigator.clipboard.writeText(`£${grandTotal().toFixed(2)}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // ── Save as template ─────────────────────────────────────────

  const saveAsTemplate = async () => {
    setSavingTemplate(true)
    setNameModal({
      title: 'Name this template',
      placeholder: 'Template name…',
      defaultValue: project?.name || '',
      confirmLabel: 'Save template',
      onConfirm: async (templateName) => {

    // Get current user
    const { data: { user } } = await supabase.auth.getUser()

    // Create template project row
    const { data: template, error } = await supabase.from('projects').insert({
      user_id: user.id,
      name: templateName.trim(),
      status: 'active',
      is_template: true,
      day_rate: project?.day_rate,
      labour_markup_pct: project?.labour_markup_pct,
    }).select().single()
    if (error || !template) { setSavingTemplate(false); return }

    // Copy line items
    if (lineItems.length > 0) {
      await supabase.from('line_items').insert(
        lineItems.map(({ id, project_id, created_at, updated_at, ...rest }) => ({
          ...rest, project_id: template.id
        }))
      )
    }

    // Copy labour lines
    if (labourLines.length > 0) {
      await supabase.from('labour_lines').insert(
        labourLines.map(({ id, project_id, created_at, updated_at, ...rest }) => ({
          ...rest, project_id: template.id
        }))
      )
    }

    // Auto-add line items to catalogue (with price_updated_at timestamp)
    const now = new Date().toISOString()
    const seenDescs = new Set()
    for (const item of lineItems) {
      if (!item.description?.trim()) continue
      const descKey = item.description.toLowerCase().trim() + '|' + item.category
      if (seenDescs.has(descKey)) continue
      seenDescs.add(descKey)
      // Use limit(1) rather than .single() — .single() errors when 2+ rows exist, making existing appear null
      const { data: rows } = await supabase.from('item_catalogue')
        .select('id').eq('user_id', user.id)
        .eq('description', item.description).eq('category', item.category).limit(1)
      if (!rows?.length) {
        await supabase.from('item_catalogue').insert({
          user_id: user.id,
          category: item.category,
          description: item.description,
          unit_cost: item.unit_cost,
          default_qty: item.quantity || 0,
          is_active: true,
          sort_order: 0,
          price_updated_at: now,
        })
      }
    }

    setSavingTemplate(false)
    setSavedTemplate(true)
    setTimeout(() => setSavedTemplate(false), 3000)
      }
    })
  }

  const openAddItems = async (catId) => {
    setAddItemsDialog(catId)
    setAddItemsTab('catalogue')
    // Fetch all My Items, exclude blank and those already in target section
    const { data } = await supabase.from('item_catalogue').select('*')
      .eq('user_id', project.user_id).order('sort_order')
    const inTargetSection = new Set(
      lineItems.filter(i => i.category === catId).map(i => i.description.toLowerCase().trim())
    )
    const available = (data || []).filter(item =>
      item.description?.trim() &&
      item.category === catId &&
      !inTargetSection.has(item.description.toLowerCase().trim())
    )
    setInactiveCatalogueItems(available)
  }

  const addFromInactiveCatalogue = async (catItem, targetCatId) => {
    const descKey = catItem.description.toLowerCase().trim()

    // Guard: if this description is already in the section (added manually or earlier this session), skip
    const alreadyInSection = lineItems.some(i =>
      i.category === targetCatId && i.description.toLowerCase().trim() === descKey
    )
    // Remove all catalogue entries sharing this description so duplicates don't linger in the picker
    setInactiveCatalogueItems(prev => prev.filter(i => i.description.toLowerCase().trim() !== descKey))
    if (alreadyInSection) return

    const { data: currentItems } = await supabase.from('line_items').select('id').eq('project_id', idRef.current).eq('category', targetCatId)
    const sortOrder = (currentItems || []).length

    const { data: newItem, error } = await supabase.from('line_items').insert({
      project_id: idRef.current,
      category: targetCatId,
      description: catItem.description,
      unit_cost: catItem.unit_cost,
      quantity: catItem.default_qty || 1,
      procurement: 'order',
      sort_order: sortOrder,
      catalogue_item_id: catItem.id,
    }).select().single()

    if (!error && newItem) {
      setLineItems(prev => [...prev, newItem])
    }
  }

  const submitSupplierRequest = async () => {
    if (!supplierInput.trim()) return
    const { data: { user } } = await supabase.auth.getUser()
    await supabase.from('supplier_requests').insert({
      user_id: project.user_id, supplier_name: supplierInput.trim(),
    })
    await fetch('/api/suggest-supplier', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ suggestion: supplierInput.trim(), userEmail: user?.email }),
    })
    setSupplierSent(true)
    setTimeout(() => { setSupplierDialog(false); setSupplierSent(false); setSupplierInput('') }, 2500)
  }

  // ── Section management ───────────────────────────────────────────
  const [editingSectionId, setEditingSectionId] = useState(null)
  const [addingSectionInline, setAddingSectionInline] = useState(false)
  const [newSectionNameInput, setNewSectionNameInput] = useState('')

  const addSection = async (name) => {
    if (!name?.trim()) return
    const slug = `custom_${Date.now()}`
    const colors = ['#2ecc71','#e67e22','#3498db','#1abc9c','#e91e63','#9c27b0']
    const color = colors[sections.length % colors.length]
    let newSection = null
    try {
      const { data, error } = await supabase.from('project_sections').insert({
        project_id: idRef.current, name: name.trim(), slug, color, sort_order: sections.length + 1,
      }).select().single()
      if (!error && data) newSection = data
    } catch(e) {}
    // Fallback: create section in state even if table missing
    if (!newSection) newSection = { id: slug, project_id: idRef.current, name: name.trim(), slug, color, sort_order: sections.length + 1 }
    setSections(prev => [...prev, newSection])
    // New sections start empty — user can add items via "+ Add item"
  }

  const renameSection = async (sectionId, newName) => {
    if (!newName.trim()) { setEditingSectionId(null); return }
    await supabase.from('project_sections').update({ name: newName.trim() }).eq('id', sectionId)
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, name: newName.trim() } : s))
    setEditingSectionId(null)
  }

  // ── Toggle categories ────────────────────────────────────────

  const toggleCategory = (catId) => {
    setOpenCategories(prev =>
      prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
    )
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', fontFamily: 'var(--font-mono)', color: 'var(--ink-3)', fontSize: '0.75rem', letterSpacing: '2px' }}>
      LOADING…
    </div>
  )

  return (
    <>
      <div className="wrap">
        {/* ── Main column ── */}
        <main>
          {/* Project header */}
          <div className="proj-head">
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Template badge */}
              {project.is_template && (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--green)', border: '1px solid var(--green)', borderRadius: '4px', padding: '2px 7px', display: 'inline-block', marginBottom: '0.4rem' }}>
                  Template
                </div>
              )}
              <input
                className="proj-title"
                style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', display: 'block' }}
                value={project.name}
                onChange={e => setProject(p => ({ ...p, name: e.target.value }))}
                onBlur={e => updateProject('name', e.target.value)}
              />
              {!project.is_template && (
                <div className="proj-sub">
                  <div className="meta">
                    <span className="meta-l">Client</span>
                    <input
                      className="meta-v"
                      style={{ border: 'none', background: 'transparent', outline: 'none', minWidth: 0 }}
                      value={project.client_name || ''}
                      onChange={e => setProject(p => ({ ...p, client_name: e.target.value }))}
                      onBlur={e => updateProject('client_name', e.target.value)}
                      placeholder="Client name"
                    />
                  </div>
                  <div className="meta">
                    <span className="meta-l">Address</span>
                    <input
                      className="meta-v"
                      style={{ border: 'none', background: 'transparent', outline: 'none', minWidth: 0 }}
                      value={project.client_address || ''}
                      onChange={e => setProject(p => ({ ...p, client_address: e.target.value }))}
                      onBlur={e => updateProject('client_address', e.target.value)}
                      placeholder="Client address"
                    />
                  </div>
                  <div className="meta">
                    <span className="meta-l">Proposal #</span>
                    <input
                      className="meta-v"
                      style={{ border: 'none', background: 'transparent', outline: 'none', minWidth: 0 }}
                      value={project.proposal_number || ''}
                      onChange={e => setProject(p => ({ ...p, proposal_number: e.target.value }))}
                      onBlur={e => updateProject('proposal_number', e.target.value)}
                      placeholder="—"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ⋯ menu */}
            <div style={{ position: 'relative', flexShrink: 0 }} ref={menuRef}>
              <button className="more" onClick={() => setMenuOpen(o => !o)} title="More options">⋯</button>
              {menuOpen && (
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', background: 'var(--surface)', border: '1px solid var(--line-2)', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', zIndex: 50, minWidth: '210px', overflow: 'hidden' }}>
                  <button style={{ display: 'block', width: '100%', padding: '0.55rem 0.9rem', fontSize: '0.82rem', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', color: 'var(--ink)', whiteSpace: 'nowrap' }} onClick={openAsNewProject}>Open as New Project</button>
                  {!project.is_template && (
                    <button style={{ display: 'block', width: '100%', padding: '0.55rem 0.9rem', fontSize: '0.82rem', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', color: 'var(--ink)', whiteSpace: 'nowrap' }} onClick={() => { setMenuOpen(false); saveAsTemplate() }}>Save as Template</button>
                  )}
                  <div style={{ height: '1px', background: 'var(--line)', margin: '0.2rem 0' }} />
                  <button style={{ display: 'block', width: '100%', padding: '0.55rem 0.9rem', fontSize: '0.82rem', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', color: 'var(--ink)', whiteSpace: 'nowrap' }} onClick={updatePrices} disabled={updatingPrices}>
                    {updatingPrices ? 'Updating…' : 'Update Prices from My Items'}
                  </button>
                </div>
              )}
              {pricesUpdatedMsg && (
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', background: 'var(--header)', color: 'var(--cream)', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', padding: '5px 10px', borderRadius: '5px', whiteSpace: 'nowrap', zIndex: 50 }}>
                  {pricesUpdatedMsg}
                </div>
              )}
            </div>
          </div>

          {/* Categories — dynamic sections */}
          {sections.map(cat => {
            const catId = cat.slug || cat.id
            const items = lineItems.filter(i => i.category === catId).sort((a, b) => a.sort_order - b.sort_order)
            const total = catId === 'labour'
              ? labourTotal() + marginOnLabour()
              : items.reduce((s, i) => s + (i.unit_cost || 0) * (i.quantity || 0), 0)
            const isOpen = openCategories.includes(catId)
            const isExtra = cat.extra || cat.slug === 'fixings'
            const cols = isExtra ? COLS_EXTRA : COLS_BASE

            return (
              <div
                key={cat.id}
                className={'section' + (isOpen ? '' : ' collapsed')}
                style={{
                  '--cat': catColor(cat),
                  outline: dragOverSectionId === cat.id ? '2px solid var(--green)' : undefined,
                  opacity: dragSectionId === cat.id ? 0.4 : 1,
                }}
                onDragOver={e => handleSectionDragOver(e, cat.id)}
                onDrop={e => handleSectionDrop(e, cat.id)}
                onDragEnd={() => { setDragSectionId(null); setDragOverSectionId(null) }}
              >
                {/* Section header */}
                <div className="sec-head" onClick={() => toggleCategory(catId)}>
                  <span
                    className="sec-dot"
                    style={{ cursor: 'grab' }}
                    draggable
                    onDragStart={e => handleSectionDragStart(e, cat.id)}
                    title="Drag to reorder"
                  />
                  {editingSectionId === cat.id ? (
                    <input
                      className="sec-title"
                      style={{ border: 'none', background: 'transparent', outline: '1px solid var(--green)', borderRadius: '3px', padding: '0 4px' }}
                      defaultValue={cat.name}
                      autoFocus
                      onClick={e => e.stopPropagation()}
                      onBlur={e => renameSection(cat.id, e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') renameSection(cat.id, e.target.value)
                        if (e.key === 'Escape') setEditingSectionId(null)
                        e.stopPropagation()
                      }}
                    />
                  ) : (
                    <span
                      className="sec-title"
                      onDoubleClick={e => { e.stopPropagation(); setEditingSectionId(cat.id) }}
                      title="Double-click to rename"
                    >
                      {cat.name}
                    </span>
                  )}
                  <span className="sec-count">{catId === 'labour' ? labourLines.length : items.length}</span>
                  <div className="sec-right">
                    <span className="sec-total tnum">£{total.toFixed(2)}</span>
                    <span className="chev">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 5 7 9 11 5" />
                      </svg>
                    </span>
                  </div>
                </div>

                {/* Animated section body */}
                <SectionBody isOpen={isOpen}>
                  {catId === 'labour' ? (
                    <LabourSection
                      project={project}
                      labourLines={labourLines}
                      timeTasks={timeTasks}
                      taskCatalogue={taskCatalogue}
                      onTaskCatalogueSave={item => setTaskCatalogue(prev => [...prev, item])}
                      onLabourLinesChange={setLabourLines}
                      onTimeTasksChange={setTimeTasks}
                      onProjectUpdate={(field, value, save) => {
                        const parsed = parseFloat(value)
                        const fallback = field === 'day_rate' ? 350 : (field === 'labour_markup_pct' ? 10 : 0)
                        const toSave = isNaN(parsed) ? fallback : parsed
                        if (save) updateProject(field, toSave)
                        else setProject(p => ({ ...p, [field]: toSave }))
                      }}
                      userId={project.user_id}
                      hoursPerDay={project.hours_per_day || 8}
                      inline
                    />
                  ) : (
                    <>
                      {/* Column headers */}
                      <div className="items-head" style={{ gridTemplateColumns: cols }}>
                        <span />
                        <span className="col-h">Item</span>
                        {isExtra && <span className="col-h c">Supplier</span>}
                        {isExtra && <span className="col-h c">SKU</span>}
                        {isExtra && <span className="col-h c">Unit</span>}
                        <span className="col-h r">Unit cost</span>
                        <span className="col-h r">Qty</span>
                        <span className="col-h r">Total</span>
                        <span />
                      </div>

                      {/* Empty state */}
                      {items.length === 0 && (
                        <div style={{ padding: '1.5rem 1.25rem', textAlign: 'center', color: 'var(--ink-3)', fontSize: '0.72rem' }}>
                          No items yet
                        </div>
                      )}

                      {/* Line items */}
                      {items.map((item) => (
                        <div
                          key={item.id}
                          draggable={false}
                          onDragOver={e => handleLineDragOver(e, item.id)}
                          onDrop={e => handleLineDrop(e, item.id, catId)}
                          style={{
                            opacity: dragLineId === item.id ? 0.4 : 1,
                            borderTop: dragOverLineId === item.id ? '2px solid var(--green)' : '2px solid transparent',
                          }}
                        >
                          <LineItem
                            item={item}
                            onUpdate={updateLineItem}
                            onDelete={deleteLineItem}
                            onAddBelow={() => addLineItemAfter(catId, item.sort_order)}
                            onDragStart={e => handleLineDragStart(e, item.id)}
                            userId={project.user_id}
                            onHartEdit={item.hart_order_id ? () => openHartForEdit(item) : null}
                          />
                        </div>
                      ))}

                      {/* Add bar */}
                      <div className="addbar">
                        <button className="add-btn" onClick={() => addLineItem(catId)}>
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="6" y1="1" x2="6" y2="11"/><line x1="1" y1="6" x2="11" y2="6"/></svg>
                          Add item
                        </button>
                        <button className="add-btn" onClick={() => openAddItems(catId)}>
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="6" y1="1" x2="6" y2="11"/><line x1="1" y1="6" x2="11" y2="6"/></svg>
                          Add from My Items
                        </button>
                        {catId === 'fixings' && (
                          <button
                            className="supplier-chip"
                            onClick={() => setHartOpen(o => !o)}
                          >
                            <span className="pulse" />
                            Hart Wholesale — Blum
                          </button>
                        )}
                        {catId === 'fixings' && (
                          <button className="add-btn" onClick={() => setSupplierDialog(true)}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="6" y1="1" x2="6" y2="11"/><line x1="1" y1="6" x2="11" y2="6"/></svg>
                            Add new supplier
                          </button>
                        )}
                      </div>

                      {/* My Items picker */}
                      {addItemsDialog === catId && (
                        <div style={{ borderTop: '1px solid var(--line)', background: 'var(--surface)', maxHeight: '340px', overflowY: 'auto' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 1.25rem', borderBottom: '1px solid var(--line)', position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 }}>
                            <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>My Items</span>
                            <button style={{ fontSize: '12px', color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setAddItemsDialog(null)}>× Close</button>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {inactiveCatalogueItems.length === 0
                              ? <div style={{ fontSize: '0.72rem', color: 'var(--ink-3)', padding: '0.75rem 1.25rem', fontStyle: 'italic' }}>No items in My Items for this section</div>
                              : inactiveCatalogueItems.map(item => (
                                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.4rem 1.25rem', borderBottom: '1px solid var(--line)' }}>
                                    <span style={{ fontSize: '0.82rem', flex: 1 }}>{item.description}</span>
                                    <span style={{ fontSize: '0.72rem', color: 'var(--ink-3)', fontVariantNumeric: 'tabular-nums' }}>£{item.unit_cost}</span>
                                    <button className="add-btn" style={{ padding: '4px 10px', fontSize: '12px' }} onClick={() => addFromInactiveCatalogue(item, catId)}>+ Add</button>
                                  </div>
                                ))
                            }
                          </div>
                        </div>
                      )}

                      {/* Hart catalogue inline in fixings */}
                      {catId === 'fixings' && hartOpen && (
                        <div style={{ borderTop: '2px solid var(--green)', background: 'var(--green-soft)' }} data-hart-panel>
                          <HartCatalogue
                            onAddItems={handleHartItems}
                            onClose={() => { setHartOpen(false); setHartEditBasket(null) }}
                            userId={project.user_id}
                            initialBasket={hartEditBasket}
                          />
                        </div>
                      )}
                    </>
                  )}
                </SectionBody>
              </div>
            )
          })}

          {/* Add new section — inline */}
          {addingSectionInline ? (
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
              <input
                style={{ flex: 1, border: '1.5px solid var(--green)', borderRadius: 'var(--radius-ctl)', padding: '5px 8px', fontSize: '0.82rem', outline: 'none', minWidth: 0, background: 'var(--surface)', color: 'var(--ink)' }}
                value={newSectionNameInput}
                autoFocus
                placeholder="Section name…"
                onChange={e => setNewSectionNameInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { addSection(newSectionNameInput); setAddingSectionInline(false); setNewSectionNameInput('') }
                  if (e.key === 'Escape') { setAddingSectionInline(false); setNewSectionNameInput('') }
                }}
              />
              <button className="cta cta-primary" style={{ padding: '6px 16px', fontSize: '13px' }} onClick={() => { addSection(newSectionNameInput); setAddingSectionInline(false); setNewSectionNameInput('') }}>Add</button>
              <button className="cta cta-ghost" style={{ padding: '6px 12px', fontSize: '13px' }} onClick={() => { setAddingSectionInline(false); setNewSectionNameInput('') }}>×</button>
            </div>
          ) : (
            <button
              style={{ display: 'block', width: '100%', textAlign: 'center', fontSize: '12px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--green)', background: 'none', border: '1.5px dashed var(--line-2)', borderRadius: 'var(--radius)', padding: '0.6rem', cursor: 'pointer', marginBottom: '0.6rem', transition: 'border-color 0.15s' }}
              onClick={() => setAddingSectionInline(true)}
            >
              + Add new section
            </button>
          )}

          {/* Add supplier dialog */}
          {supplierDialog && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSupplierDialog(false)}>
              <div style={{ background: 'var(--surface)', borderRadius: '12px', padding: '1.5rem', maxWidth: '420px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--ink)' }}>Add a supplier</div>
                {supplierSent ? (
                  <div style={{ fontSize: '0.85rem', color: 'var(--green)', padding: '1rem 0', textAlign: 'center' }}>Thanks! We'll investigate your suggestion.</div>
                ) : (
                  <>
                    <p style={{ fontSize: '0.83rem', color: 'var(--ink-2)', lineHeight: 1.6, marginBottom: '0.75rem' }}>
                      Tell us which supplier you'd like to see in Akkord.
                    </p>
                    <input
                      style={{ width: '100%', border: '1.5px solid var(--line-2)', borderRadius: 'var(--radius-ctl)', padding: '0.5rem 0.75rem', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box', background: 'var(--surface)', color: 'var(--ink)' }}
                      placeholder="e.g. Häfele, Grass, Hettich…"
                      value={supplierInput}
                      onChange={e => setSupplierInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && submitSupplierRequest()}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                      <button className="cta cta-primary" style={{ flex: 1, padding: '10px' }} onClick={submitSupplierRequest}>Submit</button>
                      <button className="cta cta-ghost" style={{ flex: 1, padding: '10px' }} onClick={() => setSupplierDialog(false)}>Cancel</button>
                    </div>
                    <p style={{ fontSize: '0.72rem', color: 'var(--ink-3)', lineHeight: 1.5, marginTop: '0.75rem', marginBottom: 0 }}>
                      Please note: for technical reasons, not all supplier catalogues are available for inclusion in Akkord. We will investigate your suggestion.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {nameModal && (
            <NameModal
              config={nameModal}
              onClose={() => setNameModal(null)}
            />
          )}
        </main>

        {/* ── Summary sidebar ── */}
        <aside className="summary-col">
          <div className="summary">
            <div className="sum-title">Project Summary</div>

            {/* Materials breakdown */}
            <div className="sum-group">
              <div className="sum-label">Materials</div>
              <div className="sum-row">
                <span className="k">Cost</span>
                <span className="v tnum">£{allMaterialsTotal().toFixed(2)}</span>
              </div>
              <div className="sum-row muted">
                <span className="k">Mark-up ({project?.material_markup_pct ?? 0}%)</span>
                <span className="v tnum">£{materialMarkup().toFixed(2)}</span>
              </div>
              <div className="sum-row sub">
                <span className="k">Subtotal</span>
                <span className="v tnum">£{(allMaterialsTotal() + materialMarkup()).toFixed(2)}</span>
              </div>
            </div>

            {/* Labour breakdown */}
            <div className="sum-group">
              <div className="sum-label">Labour</div>
              <div className="sum-row">
                <span className="k">Cost</span>
                <span className="v tnum">£{labourTotal().toFixed(2)}</span>
              </div>
              <div className="sum-row muted">
                <span className="k">Mark-up ({project?.labour_markup_pct ?? 0}%)</span>
                <span className="v tnum">£{marginOnLabour().toFixed(2)}</span>
              </div>
              <div className="sum-row sub">
                <span className="k">Subtotal</span>
                <span className="v tnum">£{(labourTotal() + marginOnLabour()).toFixed(2)}</span>
              </div>
            </div>

            {/* Mark-up & VAT controls */}
            <div className="sum-group">
              <MarkupPanel
                project={project}
                borderless
                onUpdate={(field, value, save) => {
                  if (field === 'vat_registered') {
                    updateProject('vat_registered', value)
                  } else {
                    const parsed = parseFloat(value)
                    const toSave = isNaN(parsed) ? 0 : parsed
                    setProject(p => ({ ...p, [field]: toSave }))
                    if (save) updateProject(field, toSave)
                  }
                }}
              />
            </div>

            {/* Combined + VAT */}
            <div className="sum-combined">
              <div className="sum-row">
                <span className="k">Combined</span>
                <span className="v tnum">£{(allMaterialsTotal() + materialMarkup() + labourTotal() + marginOnLabour()).toFixed(2)}</span>
              </div>
              {project?.vat_registered && (
                <div className="sum-row vat">
                  <span className="k">VAT ({project?.vat_rate ?? 20}%)</span>
                  <span className="v tnum">£{vatAmount().toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Project total */}
            <div className="total-block">
              <div className="total-l">Project Total</div>
              <div className="total-v tnum">£{grandTotal().toFixed(2)}</div>
            </div>

            {/* CTA buttons */}
            <div className="cta-stack">
              <button className="cta cta-primary" onClick={() => navigate(`/projects/${id}/quote`)}>
                Create / Edit Quote
              </button>
              <button className="cta cta-dark" onClick={copyTotal}>
                {copied ? 'Copied!' : 'Copy total'}
              </button>
              <button className="cta cta-ghost" onClick={() => navigate('/projects')}>
                Back to projects
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Undo toast */}
      {undoToast && (
        <div className={'toast show'}>
          <span>{undoToast.label}</span>
          <button
            style={{ background: 'none', border: '1px solid rgba(255,255,255,0.35)', color: 'inherit', padding: '0.25rem 0.7rem', borderRadius: '4px', fontSize: '13px', cursor: 'pointer', flexShrink: 0 }}
            onClick={undoToast.restore}
          >
            Undo
          </button>
        </div>
      )}
    </>
  )
}
